"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  Flame,
  Sparkles,
  Clock,
  Target,
  AlertTriangle,
  Activity,
  TrendingUp,
  ArrowRight,
  Crown,
  Mic,
  Layers,
  Eye,
  CheckCircle2,
  BookOpen,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { TimelineBlock } from "@/lib/blocks/timeline-block";
import { CounterBlock } from "@/lib/blocks/counter-block";
import { PillarGridBlock } from "@/lib/blocks/pillar-grid-block";

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
};

const EVIDENCE = [
  { metric: "85-95", suffix: "%", label: "Crisis Success", sub: "Verified outcomes", accent: "amber-gold" },
  { metric: "5", suffix: " min", label: "Total Response Time", sub: "Detection to deployment", accent: "red" },
  { metric: "30-60", suffix: " days", label: "Recovery to Baseline", sub: "67% faster than traditional", accent: "emerald" },
  { metric: "500", suffix: "+", label: "Response Playbooks", sub: "Battle-tested frameworks", accent: "purple" },
];

const PRINCIPLES = [
  { num: "01", title: "Defensive Response", body: "The crisis is real, the damage is contained, and the priority is to prevent escalation. Factual correction and targeted counter-narratives.", accent: "red" },
  { num: "02", title: "Counter-Narrative Deployment", body: "The crisis has gained traction and the narrative has been shaped by others. Alternative perspectives, verified facts, strategic messaging to rebalance perception.", accent: "purple" },
  { num: "03", title: "Offensive Redirection", body: "The crisis presents an opportunity to reframe the narrative entirely. Proactive messaging, strategic disclosures, and narrative engineering that transforms the crisis into a platform.", accent: "cyan" },
  { num: "04", title: "Strategic Silence", body: "Some crises are best addressed through calculated absence. Monitoring without public response, allowing the news cycle to exhaust itself while internal intelligence tracks evolution.", accent: "emerald" },
  { num: "05", title: "Transformative Response", body: "The crisis is severe, the stakes are existential. Full crisis defense architecture activates simultaneously. The entity emerges stronger than before the crisis occurred.", accent: "amber-gold" },
];

const RECOVERY_CURVES = [
  { num: "01", title: "Immediate Containment", body: "0-24 hours. Containment protocols reduce negative sentiment velocity by 70-85%. The crisis does not disappear. But its ability to propagate is dramatically reduced." },
  { num: "02", title: "Narrative Stabilization", body: "1-7 days. Counter-narratives deployed, fact-based corrections disseminated, influencer defense network activated. The dominant narrative shifts from crisis-driven to response-driven." },
  { num: "03", title: "Sentiment Recovery", body: "7-30 days. All six perception dimensions tracked individually and in aggregate. The sentiment recovery curve typically reaches 60-75% of pre-crisis baseline within thirty days." },
  { num: "04", title: "Reputation Restoration", body: "30-60 days. Restoration of reputation to pre-crisis baseline. Sixty-seven percent faster than the 90 to 180 days traditional approaches require." },
  { num: "05", title: "Competitive Transformation", body: "60-180 days. Organizations that survive a crisis through this discipline emerge stronger. Validated resilience becomes a permanent competitive differentiator." },
];

const CATEGORIES = [
  { title: "Negative Press & Media Crisis", body: "Adverse coverage in major outlets, investigative journalism, exposés, editorial attacks. Media relations and strategic engagement with editorial decision-makers.", accent: "red" },
  { title: "Coordinated Smear Campaigns", body: "Organized, multi-platform attacks designed to damage reputation through sustained negative messaging. Intelligence-driven attribution and counter-campaign deployment.", accent: "purple" },
  { title: "Viral Misinformation", body: "False or misleading content achieving viral distribution. Rapid fact-checking, platform-specific correction deployment, and algorithmic suppression strategies.", accent: "cyan" },
  { title: "Data Breaches & Security", body: "Unauthorized access to sensitive information. Stakeholder notification, regulatory compliance, and reputation recovery through coordinated response.", accent: "emerald" },
  { title: "Executive Controversies", body: "Personal or professional controversies involving senior leaders. Persona management, narrative separation, and strategic positioning.", accent: "amber-gold" },
  { title: "Sovereign & Political Crises", body: "Government-level reputation threats, political scandals, policy controversies. Sovereign-grade discretion, multi-stakeholder coordination, and national-level narrative management.", accent: "denim" },
];

const DIMENSIONS = [
  { title: "Sentiment", body: "The emotional tone of public discourse. Identifies the drivers — fear, anger, disappointment, moral outrage. Each driver requires a different response strategy." },
  { title: "Opinion", body: "The stated positions and beliefs of audience segments. Identifies who has taken a position, what that position is, and whether it is susceptible to influence." },
  { title: "Buzz", body: "The volume and velocity of conversation. A crisis with high buzz and low sentiment is a containment priority. A crisis with declining buzz is a recovery opportunity." },
  { title: "Hearsay", body: "The informal, unofficial, often unverified information circulating. Often more dangerous than verified negative information because it cannot be fact-checked." },
  { title: "Dialogue", body: "The active conversations between audiences. Identifies specific narratives, influencers driving the conversation, and communities where the conversation is concentrated." },
  { title: "Favorability", body: "The overall balance of positive and negative perception across all channels. Determines the strategic objective: restore, maintain, or transform." },
];

export default function CrisisManagementPage() {
  return (
    <>
      <PageHero
        icon={Flame}
        overline="Crisis Management"
        overlineClassName="text-amber-gold"
        title={
          <>
            Transforming Crisis Into <span className="text-gradient-premium">Competitive Advantage.</span>
          </>
        }
        subtitle="Every organization faces crisis. Few survive it intact. The crisis management capability Dr. Jyoti Kush commands operates at three hundred eighty-four to one thousand four hundred sixteen times the speed of traditional approaches — achieving eighty-five to ninety-five percent crisis success rate and restoring organizations to baseline in thirty to sixty days."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Expertise", href: "/expertise/" }, { label: "Crisis Management" }]}
        badge="Speed is the entire game"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-5`}>
              <Sparkles className="size-3.5 text-amber-gold" />
              <span className="text-overline text-amber-gold">The Crisis Reality</span>
            </div>
            <h2 className="text-display-md mb-5">
              The first five minutes determine everything.
            </h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              At sovereign scale, crisis is not a possibility. It is a certainty. The question is not whether the crisis will come. The question is whether the organization will be ready when it does. The traditional approach — assemble a team, draft a statement, hold a press conference — is not merely outdated. It is operationally lethal.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              The crisis management capability activates the moment a crisis is detected. Sub-five-minute total response time. Pattern-matched response drawing on ten thousand-plus crisis patterns. Three-pillar architecture: containment, intelligence, and security engaging simultaneously. Recovery to baseline in thirty to sixty days. The crisis becomes an opportunity for narrative reinforcement.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            {EVIDENCE.map((e, i) => {
              const a = ACCENT[e.accent];
              return (
                <motion.div
                  key={e.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
                >
                  <div className={`text-display-sm font-bold ${a.text} leading-none mb-2`}>{e.metric}{e.suffix}</div>
                  <div className="text-caption font-bold uppercase tracking-wider mb-1">{e.label}</div>
                  <div className="text-caption text-muted-foreground">{e.sub}</div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-4`}>
            <Clock className="size-3.5 text-red" />
            <span className="text-overline text-red">The Golden Hour Principle</span>
          </div>
          <h2 className="text-display-md mb-4">Ninety-five percent of crisis damage occurs in the first six hours.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            In clinical medicine, the golden hour principle holds that the first sixty minutes after a traumatic injury determine whether the patient lives or dies. The crisis management equivalent is the Golden Hour: ninety-five percent of damage occurs within the first six hours.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-30px" }}
            transition={{ duration: 0.4 }}
            className="glass-card rounded-2xl p-6 bg-gradient-to-br from-red/10 to-transparent border border-red/20"
          >
            <AlertTriangle className="size-8 text-red mb-4" />
            <h3 className="text-heading-md mb-3 text-red">Without Sovereign-Grade Speed</h3>
            <p className="text-body-sm text-muted-foreground leading-relaxed">
              The crisis team is assembled two to four hours after the first public mention. By then, the damage is done. The narrative has been shaped by others. The audience has formed opinions. The organization is now fighting to recover ground it has already lost.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-30px" }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="glass-card rounded-2xl p-6 bg-gradient-to-br from-emerald/10 to-transparent border border-emerald/20"
          >
            <CheckCircle2 className="size-8 text-emerald mb-4" />
            <h3 className="text-heading-md mb-3 text-emerald">With Sovereign-Grade Speed</h3>
            <p className="text-body-sm text-muted-foreground leading-relaxed">
              An organization that can detect a crisis within sixty seconds, diagnose its vectors within three minutes, and deploy a coordinated response within five minutes has effectively neutralized ninety-five percent of potential damage before it materializes.
            </p>
          </motion.div>
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <Target className="size-3.5 text-purple" />
            <span className="text-overline text-purple">The Five Strategic Options</span>
          </div>
          <h2 className="text-display-md mb-4">Choosing the right response for the right crisis.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The clinical method demands that the crisis be diagnosed before treated. Five options calibrated to distinct crisis types, severity levels, and strategic objectives.
          </p>
        </div>
        <div className="space-y-4">
          {PRINCIPLES.map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.num}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="grid lg:grid-cols-[auto_1fr] gap-5 items-center">
                  <div className={`size-14 rounded-2xl bg-gradient-sovereign flex items-center justify-center shadow-xl ring-2 ${a.ring}`}>
                    <span className="text-display-sm font-bold text-white">{`0${i + 1}`}</span>
                  </div>
                  <div>
                    <h3 className={`text-display-sm mb-2 ${a.text}`}>{p.title}</h3>
                    <p className="text-body-md text-muted-foreground leading-relaxed">{p.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Activity className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">The Six Perception Dimensions</span>
          </div>
          <h2 className="text-display-md mb-4">Real-time visibility across every channel of public perception.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Six dimensions monitored simultaneously, providing the complete intelligence picture that enables the right response to the right audiences at the right time.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {DIMENSIONS.map((d, i) => {
            const a = ACCENT[["red", "purple", "cyan", "emerald", "amber-gold", "denim"][i]];
            return (
              <motion.div
                key={d.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`group relative glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <div className={`text-display-sm font-bold ${a.text} opacity-30 leading-none mb-3`}>{`0${i + 1}`}</div>
                <h3 className={`text-heading-md mb-2 ${a.text}`}>{d.title}</h3>
                <p className="text-caption text-muted-foreground leading-relaxed">{d.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <TrendingUp className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">The Five Recovery Curves</span>
          </div>
          <h2 className="text-display-md mb-4">From crisis to recovery. The quantified trajectory.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Crisis recovery is not an aspiration. It is a measurable trajectory with defined milestones, quantified progress, and verified outcomes.
          </p>
        </div>
        <div className="space-y-3">
          {RECOVERY_CURVES.map((r, i) => {
            const accents = ["red", "purple", "cyan", "emerald", "amber-gold"];
            const a = ACCENT[accents[i]];
            return (
              <motion.div
                key={r.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="grid lg:grid-cols-[auto_1fr_auto] gap-5 items-center">
                  <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center shrink-0 shadow-lg`}>
                    <span className="text-caption font-bold text-white">{r.num}</span>
                  </div>
                  <div>
                    <h3 className={`text-heading-md mb-1 ${a.text}`}>{r.title}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed">{r.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <Eye className="size-3.5 text-denim" />
            <span className="text-overline text-denim">Twelve Crisis Categories</span>
          </div>
          <h2 className="text-display-md mb-4">Pattern recognition across 10,000+ crisis events.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The twelve-category taxonomy is the foundation of pattern recognition capability — the intelligence that enables five-minute response in a domain where traditional approaches require five hours.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {CATEGORIES.map((c, i) => {
            const a = ACCENT[c.accent];
            return (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <h3 className={`text-heading-md mb-2 ${a.text}`}>{c.title}</h3>
                <p className="text-caption text-muted-foreground leading-relaxed">{c.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20" id="crisis-timeline" ariaLabel="Crisis Response Timeline">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-4`}>
            <Clock className="size-3.5 text-red" />
            <span className="text-overline text-red">The Crisis Response Timeline</span>
          </div>
          <h2 className="text-display-md mb-4">Six milestones. Three hundred eighty-four times faster than the alternative.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The signature horizontal progression. Each milestone has a verified response time, a verified success rate, and a verified operator. The timeline is the doctrine.
          </p>
        </div>
        <TimelineBlock
          orientation="horizontal"
          milestones={[
            { period: "T+0s", title: "Detection", body: "Crisis event detected by sub-sixty-second alert latency. Pattern-matched against ten thousand-plus crisis events. Severity classification triggers response protocol.", icon: <AlertTriangle className="size-4" />, accent: "red" },
            { period: "T+3min", title: "Diagnosis", body: "Vectors identified, threat surface mapped, audience impact assessed. Three-pillar architecture — containment, intelligence, security — engaged simultaneously.", icon: <Target className="size-4" />, accent: "purple" },
            { period: "T+5min", title: "Response Deploy", body: "Coordinated response from five hundred-plus battle-tested playbooks. Counter-narrative, containment, stakeholder notification. Total response time under five minutes.", icon: <Activity className="size-4" />, accent: "cyan" },
            { period: "T+24h", title: "Stabilization", body: "Immediate containment reduces negative sentiment velocity by seventy to eighty-five percent. Crisis no longer propagates at scale. Dominant narrative shifts to response-driven.", icon: <CheckCircle2 className="size-4" />, accent: "emerald" },
            { period: "T+30d", title: "Recovery", body: "Sixty to seventy-five percent of pre-crisis baseline restored. All six perception dimensions tracked. Sentiment recovery curve validated against projected trajectory.", icon: <TrendingUp className="size-4" />, accent: "amber-gold" },
            { period: "T+60d", title: "Transformation", body: "Full restoration to baseline. Sixty-seven percent faster than the ninety to one hundred eighty days traditional approaches require. Entity emerges stronger than before the crisis.", icon: <Sparkles className="size-4" />, accent: "denim" },
          ]}
        />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Activity className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">The Crisis Engine Capabilities</span>
          </div>
          <h2 className="text-display-md mb-4">Six capabilities activated simultaneously.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The crisis management capability is not a single response. It is six distinct capabilities firing in parallel, each calibrated to a specific failure mode, each verified under sovereign-scale pressure.
          </p>
        </div>
        <PillarGridBlock
          columns={3}
          variant="card"
          pillars={[
            { title: "Predictive Modeling", body: "Seven-day and thirty-day crisis forecasting. Eighty-nine percent crisis prediction accuracy. Preemptive action rather than reactive damage control.", icon: <Target className="size-5" />, accent: "red", metric: "89%", metricLabel: "Prediction Accuracy" },
            { title: "NLG Response Engine", body: "Automated response generation calibrated to brand voice and crisis type. Drafts deployed for human approval in under sixty seconds, with verification checkpoints.", icon: <Sparkles className="size-5" />, accent: "purple", metric: "<60s", metricLabel: "Draft Generation" },
            { title: "Pattern-Matched Playbooks", body: "Five hundred-plus pre-built response frameworks. Each pattern-matched to the specific crisis type, severity, and strategic objective. The right playbook at the right moment.", icon: <BookOpen className="size-5" />, accent: "cyan", metric: "500+", metricLabel: "Playbooks" },
            { title: "Influencer Defense Network", body: "Eight hundred fifty thousand-plus profiled advocates ready for rapid narrative activation. Authentic voices amplifying the response across communities and platforms.", icon: <Eye className="size-5" />, accent: "emerald", metric: "850K+", metricLabel: "Advocates" },
            { title: "Multi-Language Coordination", body: "Fifty-plus language capability for global crisis response. Cultural calibration embedded in every response. Localized tone without losing core message integrity.", icon: <Layers className="size-5" />, accent: "amber-gold", metric: "50+", metricLabel: "Languages" },
            { title: "Sovereign Security Posture", body: "Encrypted crisis communications. Zero-trust infrastructure for response coordination. Source protection for sensitive intelligence inputs that drive the response.", icon: <Activity className="size-5" />, accent: "denim", metric: "0", metricLabel: "Security Incidents" },
          ]}
        />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <TrendingUp className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">Verified Performance</span>
          </div>
          <h2 className="text-display-md mb-4">The numbers that close the case.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Every metric traces to operational data from real crisis engagements. The crisis is not hypothetical. The outcomes are not projected. The record is verified.
          </p>
        </div>
        <CounterBlock
          columns={4}
          stats={[
            { value: 95, suffix: "%", label: "Crisis Success Rate", description: "Eighty-five to ninety-five percent verified", accent: "amber-gold" },
            { value: 5, suffix: " min", label: "Total Response Time", description: "Detection to deployment", accent: "red" },
            { value: 60, suffix: " days", label: "Recovery to Baseline", description: "Sixty-seven percent faster than traditional", accent: "emerald" },
            { value: 500, suffix: "+", label: "Response Playbooks", description: "Battle-tested frameworks", accent: "purple" },
            { value: 384, suffix: "x", label: "Faster Than Traditional", description: "Range three hundred eighty-four to one thousand four hundred sixteen times", accent: "cyan" },
            { value: 850, suffix: "K+", label: "Advocates in Defense Network", description: "Ready for rapid narrative activation", accent: "denim" },
          ]}
        />
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-amber-gold/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-red/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Sparkles className="size-10 text-amber-gold mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Engage crisis defense.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Sovereign-grade crisis management for governments, royal families, corporations, and public figures. National security level confidentiality. Every engagement begins with diagnosis.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Begin an Engagement
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/expertise/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <Layers className="size-4" />
                All Expertise Domains
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "Expertise Hub", description: "The complete map of fifteen expertise domains. One integrated operating system.", href: "/expertise/", icon: Flame, accent: "amber-gold" },
          { title: "Perception & Reputation", description: "Engineering how the world sees you. Narrative engineering at continental scale.", href: "/expertise/perception-reputation/", icon: TrendingUp, accent: "emerald" },
          { title: "Strategic Intelligence", description: "Seeing what others cannot. The intelligence architecture powering every engagement.", href: "/expertise/strategic-intelligence/", icon: Activity, accent: "purple" },
          { title: "About Dr. Jyoti Kush", description: "The physician who became an operator. A decade of sovereign-scale leadership.", href: "/about/", icon: Crown, accent: "cyan" },
        ]}
      />
    </>
  );
}
